import { TASK_VALIDATION_RULES, TASK_MESSAGES } from '../constants/tasksConstants';

/**
 * Valida los datos de una tarea
 * @param {Object} taskData - Datos de la tarea a validar
 * @param {boolean} isQualityTemplate - Si es plantilla de calidad (requiere fase y severidad)
 * @returns {Object} - Objeto con errores de validación
 */
export const validateTaskData = (taskData, isQualityTemplate = false) => {
  const errors = {};

  // Validar nombre
  if (!taskData.name || !taskData.name.trim()) {
    errors.name = TASK_MESSAGES.ERROR.REQUIRED_FIELD;
  } else if (taskData.name.trim().length < TASK_VALIDATION_RULES.NAME.MIN_LENGTH) {
    errors.name = TASK_MESSAGES.ERROR.NAME_TOO_SHORT;
  } else if (taskData.name.trim().length > TASK_VALIDATION_RULES.NAME.MAX_LENGTH) {
    errors.name = TASK_MESSAGES.ERROR.NAME_TOO_LONG;
  }

  // Validar descripción (opcional pero con límite)
  if (
    taskData.description &&
    taskData.description.length > TASK_VALIDATION_RULES.DESCRIPTION.MAX_LENGTH
  ) {
    errors.description = TASK_MESSAGES.ERROR.DESCRIPTION_TOO_LONG;
  }

  // Validar orden de secuencia
  if (
    !taskData.sequence_order ||
    taskData.sequence_order < TASK_VALIDATION_RULES.SEQUENCE_ORDER.MIN
  ) {
    errors.sequence_order = TASK_MESSAGES.ERROR.INVALID_SEQUENCE;
  }

  // Validar porcentaje máximo
  if (taskData.max_percentage !== undefined && taskData.max_percentage !== null) {
    const maxPercentage = Number(taskData.max_percentage);
    if (
      isNaN(maxPercentage) ||
      maxPercentage < TASK_VALIDATION_RULES.PERCENTAGE.MIN ||
      maxPercentage > TASK_VALIDATION_RULES.PERCENTAGE.MAX
    ) {
      errors.max_percentage = TASK_MESSAGES.ERROR.INVALID_PERCENTAGE;
    }
  }

  // Validar porcentaje por defecto
  if (taskData.default_percentage !== undefined && taskData.default_percentage !== null) {
    const defaultPercentage = Number(taskData.default_percentage);
    if (
      isNaN(defaultPercentage) ||
      defaultPercentage < TASK_VALIDATION_RULES.PERCENTAGE.MIN ||
      defaultPercentage > TASK_VALIDATION_RULES.PERCENTAGE.MAX
    ) {
      errors.default_percentage = TASK_MESSAGES.ERROR.INVALID_PERCENTAGE;
    }

    // Validar que el porcentaje por defecto no sea mayor al máximo
    const maxPercentage = Number(taskData.max_percentage);
    if (!isNaN(maxPercentage) && defaultPercentage > maxPercentage) {
      errors.default_percentage =
        'El porcentaje por defecto no puede ser mayor al porcentaje máximo';
    }
  }

  // Validar severidad SOLO para plantillas de calidad
  if (isQualityTemplate) {
    if (!taskData.severity) {
      errors.severity = TASK_MESSAGES.ERROR.REQUIRED_FIELD;
    } else if (!['low', 'medium', 'high'].includes(taskData.severity)) {
      errors.severity = 'Severidad inválida';
    }
  }

  // Para plantillas de progreso, no validar fase ni severidad
  // La severidad siempre debe tener un valor por defecto para el backend

  return errors;
};

/**
 * Prepara los datos de la tarea para envío a la API
 * @param {Object} taskData - Datos de la tarea
 * @param {boolean} isQualityTemplate - Si es plantilla de calidad
 * @returns {Object} - Datos preparados para la API
 */
export const prepareTaskData = (taskData, isQualityTemplate = false) => {
  const cleanedData = { ...taskData };

  // Limpiar campos vacíos y espacios innecesarios
  Object.keys(cleanedData).forEach((key) => {
    if (typeof cleanedData[key] === 'string') {
      cleanedData[key] = cleanedData[key].trim();
      if (cleanedData[key] === '') {
        cleanedData[key] = null;
      }
    }
  });

  // Para plantillas de progreso, establecer valores por defecto
  if (!isQualityTemplate) {
    cleanedData.phase = null; // Permitir null para plantillas de progreso
    cleanedData.severity = cleanedData.severity || 'low'; // Valor por defecto para el backend
  }

  // Convertir porcentajes a números
  if (cleanedData.max_percentage !== undefined && cleanedData.max_percentage !== null) {
    cleanedData.max_percentage = Number(cleanedData.max_percentage);
  }
  if (cleanedData.default_percentage !== undefined && cleanedData.default_percentage !== null) {
    cleanedData.default_percentage = Number(cleanedData.default_percentage);
  }
  if (cleanedData.sequence_order !== undefined && cleanedData.sequence_order !== null) {
    cleanedData.sequence_order = Number(cleanedData.sequence_order);
  }

  // Remover campos que no deben enviarse
  delete cleanedData.uuid;
  delete cleanedData.created_at;
  delete cleanedData.updated_at;
  delete cleanedData.deleted_at;

  return cleanedData;
};

/**
 * Ordena tareas por orden de secuencia
 * @param {Array} tasks - Lista de tareas
 * @returns {Array} - Tareas ordenadas
 */
export const sortTasksBySequence = (tasks) => {
  return [...tasks].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
};

/**
 * Calcula el siguiente número de secuencia disponible
 * @param {Array} tasks - Lista de tareas existentes
 * @returns {number} - Siguiente número de secuencia
 */
export const getNextSequenceOrder = (tasks) => {
  if (!tasks || tasks.length === 0) return 1;

  const maxOrder = Math.max(...tasks.map((task) => task.sequence_order || 0));
  return maxOrder + 1;
};

/**
 * Valida si un orden de secuencia está disponible
 * @param {Array} tasks - Lista de tareas existentes
 * @param {number} sequenceOrder - Orden a validar
 * @param {string} excludeTaskId - ID de tarea a excluir de la validación
 * @returns {boolean} - True si está disponible
 */
export const isSequenceOrderAvailable = (tasks, sequenceOrder, excludeTaskId = null) => {
  return !tasks.some(
    (task) => task.sequence_order === sequenceOrder && task.uuid !== excludeTaskId
  );
};

/**
 * Formatea porcentaje para mostrar
 * @param {number} percentage - Porcentaje a formatear
 * @returns {string} - Porcentaje formateado
 */
export const formatPercentage = (percentage) => {
  if (percentage === undefined || percentage === null) return '0%';
  return `${Number(percentage).toFixed(2)}%`;
};

/**
 * Obtiene el color de la severidad
 * @param {string} severity - Nivel de severidad
 * @returns {string} - Color correspondiente
 */
export const getSeverityColor = (severity) => {
  const colors = {
    low: '#4caf50', // Verde
    medium: '#ff9800', // Naranja
    high: '#f44336', // Rojo
  };
  return colors[severity] || colors.low;
};

/**
 * Calcula estadísticas de un conjunto de tareas
 * @param {Array} tasks - Lista de tareas
 * @param {boolean} isQualityTemplate - Si es plantilla de calidad
 * @returns {Object} - Estadísticas calculadas
 */
export const calculateTaskStats = (tasks, isQualityTemplate = false) => {
  if (!tasks || tasks.length === 0) {
    return {
      total: 0,
      byPhase: {},
      bySeverity: { low: 0, medium: 0, high: 0 },
      averageMaxPercentage: 0,
      averageDefaultPercentage: 0,
    };
  }

  const stats = {
    total: tasks.length,
    byPhase: {},
    bySeverity: { low: 0, medium: 0, high: 0 },
    averageMaxPercentage: 0,
    averageDefaultPercentage: 0,
  };

  let totalMaxPercentage = 0;
  let totalDefaultPercentage = 0;

  tasks.forEach((task) => {
    // Estadísticas por fase (solo para plantillas de calidad)
    if (isQualityTemplate) {
      const phase = task.phase || 'sin_fase';
      stats.byPhase[phase] = (stats.byPhase[phase] || 0) + 1;

      // Estadísticas por severidad (solo para plantillas de calidad)
      stats.bySeverity[task.severity] = (stats.bySeverity[task.severity] || 0) + 1;
    }

    // Sumar porcentajes para calcular promedios
    totalMaxPercentage += Number(task.max_percentage || 0);
    totalDefaultPercentage += Number(task.default_percentage || 0);
  });

  // Calcular promedios
  stats.averageMaxPercentage = totalMaxPercentage / tasks.length;
  stats.averageDefaultPercentage = totalDefaultPercentage / tasks.length;

  return stats;
};

/**
 * Genera datos para reordenar tareas
 * @param {Array} tasks - Lista de tareas reordenadas
 * @returns {Array} - Array con datos para la API de reordenamiento
 */
export const generateReorderData = (tasks) => {
  return tasks.map((task, index) => ({
    uuid: task.uuid,
    sequence_order: index + 1,
  }));
};

/**
 * Filtra tareas por criterios específicos
 * @param {Array} tasks - Lista de tareas
 * @param {Object} filters - Filtros a aplicar
 * @param {boolean} isQualityTemplate - Si es plantilla de calidad
 * @returns {Array} - Tareas filtradas
 */
export const filterTasks = (tasks, filters, isQualityTemplate = false) => {
  let filtered = [...tasks];

  // Solo filtrar por fase y severidad si es plantilla de calidad
  if (isQualityTemplate) {
    if (filters.phase) {
      filtered = filtered.filter((task) => task.phase === filters.phase);
    }

    if (filters.severity) {
      filtered = filtered.filter((task) => task.severity === filters.severity);
    }
  }

  if (filters.search) {
    const searchTerm = filters.search.toLowerCase();
    filtered = filtered.filter(
      (task) =>
        task.name?.toLowerCase().includes(searchTerm) ||
        task.description?.toLowerCase().includes(searchTerm)
    );
  }

  return filtered;
};

/**
 * Determina si se deben mostrar campos específicos según el tipo de plantilla
 * @param {string} templateType - Tipo de plantilla ('progress' o 'quality')
 * @returns {Object} - Configuración de visibilidad de campos
 */
export const getFieldVisibility = (templateType) => {
  const isQuality = templateType === 'quality';

  return {
    showPhase: isQuality,
    showSeverity: isQuality,
    phaseRequired: isQuality,
    severityRequired: isQuality,
  };
};
